import React, { useState, useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { X, MapPin, Calendar, Package, DollarSign, Info, User } from 'lucide-react';
import { tripsAPI, feesAPI } from '../services/api';
import toast from 'react-hot-toast';
import { useThemeColors } from '../theme/ThemeContext';
import Select from 'react-select';
import { Country, State, City } from "country-state-city";

// Format date for HTML date input (YYYY-MM-DD) without timezone conversion
const formatDateForInput = (dateString) => {
  if (!dateString) return '';

  // If it's already in YYYY-MM-DD format, return as-is
  if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }

  // Parse the date and format it properly
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';

  // Get year, month, day in local timezone
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

// Get countries from package
const countries = Country.getAllCountries();

// Get cities for selected country
const getCountryCities = (countryCode) => {
  if (!countryCode) return [];

  try {
    console.log(`Getting cities for country: ${countryCode}`);

    // Get all states for the country
    const states = State.getStatesOfCountry(countryCode);
    console.log(`States for ${countryCode}:`, states);

    if (!states || states.length === 0) {
      console.log(`No states found for ${countryCode}`);
      return [];
    }

    // Get cities from all states
    let allCities = [];
    states.forEach(state => {
      try {
        const cities = City.getCitiesOfState(countryCode, state.isoCode);
        if (cities && cities.length > 0) {
          allCities = allCities.concat(cities);
        }
      } catch (error) {
        console.log(`Error getting cities for state ${state.isoCode}:`, error);
      }
    });

    // Extract city names and remove duplicates
    const cityNames = allCities.map(city => city.name);
    const uniqueCityNames = [...new Set(cityNames)];
    console.log(`Final cities for ${countryCode}:`, uniqueCityNames.slice(0, 20)); // Show first 20
    return uniqueCityNames.sort();
  } catch (error) {
    console.log(`Error getting cities for country ${countryCode}:`, error);
    return [];
  }
};

const EditTripForm = ({ trip, onSubmit, onCancel, isLoading }) => {
  const colors = useThemeColors();
  const { register, handleSubmit, watch, setValue, control, formState: { errors } } = useForm();
  const [feeEstimate, setFeeEstimate] = useState(null);
  const [isCalculatingFee, setIsCalculatingFee] = useState(false);
  const [selectedOriginCountry, setSelectedOriginCountry] = useState('');
  const [selectedDestinationCountry, setSelectedDestinationCountry] = useState('');
  const [originCities, setOriginCities] = useState([]);
  const [destinationCities, setDestinationCities] = useState([]);
  const [guaranteedDelivery, setGuaranteedDelivery] = useState(false);
  const [purchased, setPurchased] = useState(false);

  const watchedValues = watch();
  const toastShownRef = useRef(false);

  // Populate form with existing trip data
  useEffect(() => {
    if (trip) {
      console.log('Populating form with trip data:', trip);

      // Set country selections first
      setSelectedOriginCountry(trip.origin_country || '');
      setSelectedDestinationCountry(trip.destination_country || '');

      // Load cities for existing countries
      if (trip.origin_country) {
        const countryCode = countries.find(c => c.name === trip.origin_country)?.isoCode;
        if (countryCode) {
          const cities = getCountryCities(countryCode);
          setOriginCities(cities);
        }
      }

      if (trip.destination_country) {
        const countryCode = countries.find(c => c.name === trip.destination_country)?.isoCode;
        if (countryCode) {
          const cities = getCountryCities(countryCode);
          setDestinationCities(cities);
        }
      }

      // Set all other form values
      setValue('originCountry', trip.origin_country || '');
      setValue('destinationCountry', trip.destination_country || '');
      setValue('departureDate', trip.departure_date ? formatDateForInput(trip.departure_date) : '');
      setValue('arrivalDate', trip.arrival_date ? formatDateForInput(trip.arrival_date) : '');
      setValue('max_weight_kg', trip.max_weight_kg || '');
      setValue('max_parcels', trip.max_parcels || '');
      setValue('price_per_kg', trip.price_per_kg || '');
      setValue('description', trip.notes || trip.description || '');
      setValue('max_declared_amount', trip.max_declared_amount || '');
      setValue('max_purchase_price', trip.max_purchase_price || '');

      // Set checkbox states
      setGuaranteedDelivery(trip.guaranteed_delivery || false);
      setPurchased(trip.purchased || false);

      // Show success toast only once per trip
      if (!toastShownRef.current) {
        toast.success('Trip data loaded for editing!');
        toastShownRef.current = true;
      }
    } else {
      // Reset toast flag when trip changes
      toastShownRef.current = false;
    }
  }, [trip]);

  // Set destination city value after destinationCities are loaded
  useEffect(() => {
    if (trip && trip.destination_city) {
      if (destinationCities.length > 0) {
        // Cities are available - check if the city is in the list
        if (destinationCities.includes(trip.destination_city)) {
          setValue('destinationCity', { value: trip.destination_city, label: trip.destination_city });
        } else {
          // City was manually entered - set as object for Select component
          setValue('destinationCity', { value: trip.destination_city, label: trip.destination_city });
        }
      } else {
        // No cities available - set as string for manual input
        setValue('destinationCity', trip.destination_city);
      }
    }
  }, [destinationCities, trip, setValue]);

  // Set origin city value after originCities are loaded
  useEffect(() => {
    if (trip && trip.origin_city) {
      if (originCities.length > 0) {
        // Cities are available - set as object for Select component
        setValue('originCity', { value: trip.origin_city, label: trip.origin_city });
      } else {
        // No cities available - set as string for manual input
        setValue('originCity', trip.origin_city);
      }
    }
  }, [originCities, trip, setValue]);

  // Calculate fee estimate when relevant fields change
  useEffect(() => {
    const calculateFee = async () => {
      if (watchedValues.max_weight_kg || watchedValues.max_parcels) {
        setIsCalculatingFee(true);
        try {
          const response = await feesAPI.calculateFeeEstimate('update_trip', {
            max_weight_kg: parseFloat(watchedValues.max_weight_kg),
            max_parcels: parseInt(watchedValues.max_parcels),
          });
          setFeeEstimate(response.data.data);
        } catch (error) {
          console.error('Failed to calculate fee:', error);
        } finally {
          setIsCalculatingFee(false);
        }
      }
    };

    const timeoutId = setTimeout(calculateFee, 500);
    return () => clearTimeout(timeoutId);
  }, [watchedValues.max_weight_kg]);

  const onFormSubmit = (data) => {
    const tripData = {
      origin_city: data.originCity.value || data.originCity,
      destination_city: data.destinationCity.value || data.destinationCity,
      origin_country: selectedOriginCountry,
      destination_country: selectedDestinationCountry,
      departure_date: data.departureDate,
      arrival_date: data.arrivalDate,
      max_parcels: parseInt(data.max_parcels),
      max_weight_kg: parseFloat(data.max_weight_kg),
      guaranteed_delivery: guaranteedDelivery,
      purchased: purchased,
      notes: data.description,
      price_per_kg: parseFloat(data.price_per_kg) || 0,
      max_declared_amount: guaranteedDelivery ? parseFloat(data.max_declared_amount) || 0 : null,
      max_purchase_price: purchased ? parseFloat(data.max_purchase_price) || 0 : null,
    };

    onSubmit(tripData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-theme-card rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-theme-light">
          <h2 className="text-xl font-semibold text-theme-primary">Edit Trip</h2>
          <button
            onClick={onCancel}
            className="text-theme-tertiary hover:text-theme-secondary"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onFormSubmit)} className="p-6 space-y-6">
          {/* Origin Location */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-theme-primary text-theme-primary flex items-center">
              <MapPin className="h-5 w-5 mr-2 text-theme-success" />
              Origin Location
            </h3>

            <div>
              <label className="block text-sm font-medium text-theme-primary text-theme-secondary mb-1">
                Origin Country *
              </label>
              <Controller
                name="originCountry"
                control={control}
                rules={{ required: 'Origin country is required' }}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <Select
                      {...field}
                      options={countries.map(country => ({
                        value: country.name,
                        label: country.name,
                        isoCode: country.isoCode
                      }))}
                      placeholder="Select country"
                      className="w-full"
                      classNamePrefix="react-select"
                      onChange={(selectedOption) => {
                        const value = selectedOption?.value || '';
                        field.onChange(value);
                        setSelectedOriginCountry(value);
                        setValue('originCity', '');
                        // Load cities for selected country
                        if (value) {
                          const countryCode = countries.find(c => c.name === value)?.isoCode;
                          if (countryCode) {
                            const cities = getCountryCities(countryCode);
                            setOriginCities(cities);
                          }
                        } else {
                          setOriginCities([]);
                        }
                      }}
                      value={field.value ? { value: field.value, label: field.value } : null}
                      styles={{
                        control: (baseStyles, state) => ({
                          ...baseStyles,
                          borderColor: state.isFocused ? colors.primary[500] : '#e5e7eb',
                          '&:hover': { borderColor: colors.primary[400] },
                          boxShadow: state.isFocused ? `0 0 0 1px ${colors.primary[500]}` : 'none',
                          borderRadius: '0.375rem',
                          padding: '0.5rem 0.75rem'
                        })
                      }}
                    />
                    {error && (
                      <p className="mt-1 text-sm text-theme-error">{error.message}</p>
                    )}
                  </>
                )}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-theme-primary text-theme-secondary mb-1">
                Origin City *
              </label>
              {selectedOriginCountry && originCities.length > 0 ? (
                <Controller
                  name="originCity"
                  control={control}
                  rules={{ required: 'Origin city is required' }}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <Select
                        {...field}
                        options={originCities.map(city => ({
                          value: city,
                          label: city
                        }))}
                        placeholder="Select city"
                        className="w-full"
                        classNamePrefix="react-select"
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: state.isFocused ? colors.primary[500] : '#e5e7eb',
                            '&:hover': { borderColor: colors.primary[400] },
                            boxShadow: state.isFocused ? `0 0 0 1px ${colors.primary[500]}` : 'none',
                            borderRadius: '0.375rem',
                            padding: '0.5rem 0.75rem'
                          })
                        }}
                      />
                      {error && (
                        <p className="mt-1 text-sm text-theme-error">{error.message}</p>
                      )}
                    </>
                  )}
                />
              ) : (
                <input
                  {...register('originCity', { required: 'Origin city is required' })}
                  type="text"
                  className="w-full px-3 py-2 border border-theme-light rounded-md focus:ring-theme-primary focus:border-theme-primary"
                  placeholder="Enter city manually"
                />
              )}
              {errors.originCity && !selectedOriginCountry && (
                <p className="mt-1 text-sm text-theme-error">{errors.originCity.message}</p>
              )}
            </div>

          </div>

          {/* Destination Location */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-theme-primary text-theme-primary flex items-center">
              <MapPin className="h-5 w-5 mr-2 text-theme-error" />
              Destination Location
            </h3>

            <div>
              <label className="block text-sm font-medium text-theme-primary text-theme-secondary mb-1">
                Destination Country *
              </label>
              <Controller
                name="destinationCountry"
                control={control}
                rules={{ required: 'Destination country is required' }}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <Select
                      {...field}
                      options={countries.map(country => ({
                        value: country.name,
                        label: country.name,
                        isoCode: country.isoCode
                      }))}
                      placeholder="Select country"
                      className="w-full"
                      classNamePrefix="react-select"
                      onChange={(selectedOption) => {
                        const value = selectedOption?.value || '';
                        field.onChange(value);
                        setSelectedDestinationCountry(value);
                        setValue('destinationCity', '');
                        // Load cities for selected country
                        if (value) {
                          const countryCode = countries.find(c => c.name === value)?.isoCode;
                          if (countryCode) {
                            const cities = getCountryCities(countryCode);
                            setDestinationCities(cities);
                          }
                        } else {
                          setDestinationCities([]);
                        }
                      }}
                      value={field.value ? { value: field.value, label: field.value } : null}
                      styles={{
                        control: (baseStyles, state) => ({
                          ...baseStyles,
                          borderColor: state.isFocused ? colors.primary[500] : '#e5e7eb',
                          '&:hover': { borderColor: colors.primary[400] },
                          boxShadow: state.isFocused ? `0 0 0 1px ${colors.primary[500]}` : 'none',
                          borderRadius: '0.375rem',
                          padding: '0.5rem 0.75rem'
                        })
                      }}
                    />
                    {error && (
                      <p className="mt-1 text-sm text-theme-error">{error.message}</p>
                    )}
                  </>
                )}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-theme-primary text-theme-secondary mb-1">
                Destination City *
              </label>
              {selectedDestinationCountry && destinationCities.length > 0 ? (
                <Controller
                  name="destinationCity"
                  control={control}
                  rules={{ required: 'Destination city is required' }}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <Select
                        {...field}
                        options={destinationCities.map(city => ({
                          value: city,
                          label: city
                        }))}
                        placeholder="Select city"
                        className="w-full"
                        classNamePrefix="react-select"
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: state.isFocused ? colors.primary[500] : '#e5e7eb',
                            '&:hover': { borderColor: colors.primary[400] },
                            boxShadow: state.isFocused ? `0 0 0 1px ${colors.primary[500]}` : 'none',
                            borderRadius: '0.375rem',
                            padding: '0.5rem 0.75rem'
                          })
                        }}
                      />
                      {error && (
                        <p className="mt-1 text-sm text-theme-error">{error.message}</p>
                      )}
                    </>
                  )}
                />
              ) : (
                <input
                  {...register('destinationCity', { required: 'Destination city is required' })}
                  type="text"
                  className="w-full px-3 py-2 border border-theme-light rounded-md focus:ring-theme-primary focus:border-theme-primary"
                  placeholder="Enter city manually"
                />
              )}
              {errors.destinationCity && !selectedDestinationCountry && (
                <p className="mt-1 text-sm text-theme-error">{errors.destinationCity.message}</p>
              )}
            </div>
          </div>

          {/* Schedule */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-theme-primary flex items-center">
              <Calendar className="h-5 w-5 mr-2 text-theme-primary" />
              Schedule
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-theme-secondary mb-1">
                  Departure Date *
                </label>
                <input
                  {...register('departureDate', { required: 'Departure date is required' })}
                  type="date"
                  min={new Date().toISOString().split('T')[0]}
                                  className="w-full px-3 py-2 border border-theme-light rounded-md focus:ring-theme-primary focus:border-theme-primary"
                />
                {errors.departureDate && (
                  <p className="mt-1 text-sm text-theme-error">{errors.departureDate.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-theme-secondary mb-1">
                  Arrival Date *
                </label>
                <input
                  {...register('arrivalDate', {
                    required: 'Arrival date is required',
                    validate: (value) => {
                      const departure = watchedValues.departureDate;
                      if (departure && value <= departure) {
                        return 'Arrival date must be after departure date';
                      }
                      return true;
                    }
                  })}
                  type="date"
                  min={watchedValues.departureDate || new Date().toISOString().split('T')[0]}
                                  className="w-full px-3 py-2 border border-theme-light rounded-md focus:ring-theme-primary focus:border-theme-primary"
                />
                {errors.arrivalDate && (
                  <p className="mt-1 text-sm text-theme-error">{errors.arrivalDate.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Capacity */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-theme-primary flex items-center">
              <Package className="h-5 w-5 mr-2 text-theme-primary" />
              Capacity
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-theme-secondary mb-1">
                  Max Weight (kg) *
                </label>
                <input
                  {...register('max_weight_kg', {
                    required: 'Max weight is required',
                    min: { value: 0.1, message: 'Weight must be at least 0.1 kg' },
                    max: { value: 100, message: 'Weight cannot exceed 100 kg' },
                  })}
                  type="number"
                  step="0.1"
                                  className="w-full px-3 py-2 border border-theme-light rounded-md focus:ring-theme-primary focus:border-theme-primary"
                  placeholder="e.g., 20"
                />
                {errors.max_weight_kg && (
                  <p className="mt-1 text-sm text-theme-error">{errors.max_weight_kg.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-theme-secondary mb-1">
                  Max Parcels *
                </label>
                <input
                  {...register('max_parcels', {
                    required: 'Max parcels is required',
                    min: { value: 1, message: 'At least 1 parcel' },
                    max: { value: 20, message: 'Cannot exceed 20 parcels' },
                  })}
                  type="number"
                                  className="w-full px-3 py-2 border border-theme-light rounded-md focus:ring-theme-primary focus:border-theme-primary"
                  placeholder="e.g., 5"
                />
                {errors.max_parcels && (
                  <p className="mt-1 text-sm text-theme-error">{errors.max_parcels.message}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-theme-secondary mb-1">
                Delivery Price per kg (ETB)
              </label>
              <input
                {...register('price_per_kg', {
                  required: 'Price is required',
                  min: { value: 0, message: 'Price cannot be negative' },
                })}
                type="number"
                step="0.01"
                                className="w-full px-3 py-2 border border-theme-light rounded-md focus:ring-theme-primary focus:border-theme-primary"
                placeholder="e.g., 50.00"
              />
              {errors.price_per_kg && (
                <p className="mt-1 text-sm text-theme-error">{errors.price_per_kg.message}</p>
              )}
            </div>
          </div>

          {/* Options */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-theme-primary flex items-center">
              <Info className="h-5 w-5 mr-2 text-theme-primary" />
              Options
            </h3>

            <div className="space-y-3">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="guaranteed_delivery"
                  checked={guaranteedDelivery}
                  onChange={(e) => setGuaranteedDelivery(e.target.checked)}
                  className="h-4 w-4 text-theme-primary focus:ring-theme-primary border-theme-light rounded"
                />
                <label htmlFor="guaranteed_delivery" className="ml-2 text-sm text-theme-secondary">
                  Accept Guaranteed Delivery Requests
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="purchased"
                  checked={purchased}
                  onChange={(e) => setPurchased(e.target.checked)}
                  className="h-4 w-4 text-theme-primary focus:ring-theme-primary border-theme-light rounded"
                />
                <label htmlFor="purchased" className="ml-2 text-sm text-theme-secondary">
                  Accept Purchased Delivery Requests
                </label>
              </div>
            </div>
          </div>

          {/* Conditional Fields */}
          {guaranteedDelivery && (
            <div>
              <label className="block text-sm font-medium text-theme-secondary mb-1">
                Max Declared Amount (ETB)
              </label>
              <input
                {...register('max_declared_amount', {
                  required: guaranteedDelivery ? 'Max declared amount is required' : false,
                  min: { value: 1, message: 'Amount cannot be less than 1' },
                })}
                type="number"
                step="0.01"
                                className="w-full px-3 py-2 border border-theme-light rounded-md focus:ring-theme-primary focus:border-theme-primary"
                placeholder="e.g., 50000.00"
              />
              {errors.max_declared_amount && (
                <p className="mt-1 text-sm text-theme-error">{errors.max_declared_amount.message}</p>
              )}
            </div>
          )}

          {purchased && (
            <div>
              <label className="block text-sm font-medium text-theme-secondary mb-1">
                Max Purchase Price (ETB) *
              </label>
              <input
                {...register('max_purchase_price', {
                  required: purchased ? 'Max purchase price is required' : false,
                  min: { value: 1, message: 'Price cannot be less than 1' },
                })}
                type="number"
                step="0.01"
                                className="w-full px-3 py-2 border border-theme-light rounded-md focus:ring-theme-primary focus:border-theme-primary"
                placeholder="e.g., 1000.00"
              />
              {errors.max_purchase_price && (
                <p className="mt-1 text-sm text-theme-error">{errors.max_purchase_price.message}</p>
              )}
            </div>
          )}

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-theme-secondary mb-1">
              Description
            </label>
            <textarea
              {...register('description')}
              rows={3}
                              className="w-full px-3 py-2 border border-theme-light rounded-md focus:ring-theme-primary focus:border-theme-primary"
              placeholder="Describe your trip, transportation method, etc..."
            />
          </div>

          {/* Fee Estimate */}
          {feeEstimate && (
            <div className="bg-theme-primary-100 border border-theme-primary-200 rounded-md p-4">
              <div className="flex items-start">
                <DollarSign className="h-5 w-5 text-theme-primary mt-0.5 mr-2" />
                <div>
                  <h4 className="text-sm font-medium text-theme-primary text-theme-primary">Fee Estimate</h4>
                  <div className="mt-1 text-sm text-theme-secondary">
                    <div>Posting Fee: {feeEstimate.posting_fee || 0} ETB</div>
                    <div className="font-medium text-theme-primary mt-1">
                      Total: {feeEstimate.total_cost || 0} ETB
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Info Note */}
          <div className="bg-theme-neutral-100 border border-theme-light rounded-md p-4">
            <div className="flex items-start">
              <Info className="h-5 w-5 text-theme-secondary mt-0.5 mr-2" />
              <div className="text-sm text-theme-secondary">
                <p className="font-medium text-theme-primary">Important Information:</p>
                <ul className="mt-1 list-disc list-inside space-y-1 text-theme-primary">
                  <li>Changes will update your trip listing</li>
                  <li>You can cancel the trip before any applications are received</li>
                  <li>Be realistic about your capacity and schedule</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4 pt-4 border-t border-theme-light">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-theme-secondary border border-theme-light rounded-md hover:bg-theme-neutral-100"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-theme-primary text-theme-inverse rounded-md hover:bg-theme-primary-hover disabled:opacity-50"
              disabled={isLoading || isCalculatingFee}
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditTripForm;
